import styled from 'styled-components'
import { Wrapper, P } from '@farewill/ui'
import { COLOR, GTR, FONT } from '@farewill/ui/tokens'

import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
import { QuoteResponseDataProbate } from 'lib/types'

import Dinero from 'dinero.js'
Dinero.globalLocale = 'en-GB'
Dinero.defaultCurrency = 'GBP'

const StyledBreakdownRow = styled(Wrapper)`
  display: grid;
  grid-template-columns: 7fr 3fr;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${COLOR.GREY.LIGHT};
  }
`

const StyledBreakdownCol = styled(Wrapper)`
  font-size: ${FONT.SIZE.XS};
  padding: ${GTR.XXS} 0;

  &:nth-child(odd) {
    border-right: 1px solid ${COLOR.GREY.LIGHT};
    padding-right: ${GTR.XS};
  }

  &:nth-child(even) {
    padding-left: ${GTR.XS};
  }

  ${({ $strong }) => $strong && `font-weight: bold;`}
`

const ProbateOnlyQuoteBreakdown = ({
  quote,
  calculatedPrices,
}: {
  quote: QuoteResponseDataProbate
  calculatedPrices: {
    totalInPence: number
    basePriceBeforeVatInPence: number
    complexityBeforeVatInPence: number
    amountBeforeVatAndDiscountInPence: number
    vatInPence: number
    disbursementsPriceInPence: number
  }
}) => {
  const { grantOnly: grantOnlyQuote } = quote.attributes
  const { isComplex, discountAmountInPence } = grantOnlyQuote
  return (
    <>
      <P size="S" margin={[0, 0, 'XS']}>
        Breakdown (internal use only)
      </P>
      <StyledBreakdownRow>
        <StyledBreakdownCol>Service fee (ex VAT) </StyledBreakdownCol>
        <StyledBreakdownCol>
          {formatCurrency({
            value: calculatedPrices.basePriceBeforeVatInPence,
            valueInPence: true,
            showPence: hasDecimals(
              calculatedPrices.basePriceBeforeVatInPence / 100
            ),
          })}
        </StyledBreakdownCol>
      </StyledBreakdownRow>
      {isComplex && (
        <StyledBreakdownRow>
          <StyledBreakdownCol>Complexity (ex VAT) </StyledBreakdownCol>
          <StyledBreakdownCol>
            {formatCurrency({
              value: calculatedPrices.complexityBeforeVatInPence,
              valueInPence: true,
              showPence: hasDecimals(
                calculatedPrices.complexityBeforeVatInPence / 100
              ),
            })}
          </StyledBreakdownCol>
        </StyledBreakdownRow>
      )}
      <StyledBreakdownRow>
        <StyledBreakdownCol $strong>Fee exc. VAT</StyledBreakdownCol>
        <StyledBreakdownCol $strong>
          {formatCurrency({
            value: calculatedPrices.amountBeforeVatAndDiscountInPence,
            valueInPence: true,
            showPence: hasDecimals(
              calculatedPrices.amountBeforeVatAndDiscountInPence / 100
            ),
          })}
        </StyledBreakdownCol>
      </StyledBreakdownRow>
      <StyledBreakdownRow>
        <StyledBreakdownCol>20% VAT</StyledBreakdownCol>
        <StyledBreakdownCol>
          {formatCurrency({
            value: calculatedPrices.vatInPence,
            valueInPence: true,
            showPence: hasDecimals(calculatedPrices.vatInPence / 100),
          })}
        </StyledBreakdownCol>
      </StyledBreakdownRow>
      <StyledBreakdownRow>
        <StyledBreakdownCol>Standard disbursements</StyledBreakdownCol>
        <StyledBreakdownCol>
          {formatCurrency({
            value: calculatedPrices.disbursementsPriceInPence,
            valueInPence: true,
            showPence: hasDecimals(
              calculatedPrices.disbursementsPriceInPence / 100
            ),
          })}
        </StyledBreakdownCol>
      </StyledBreakdownRow>
      {discountAmountInPence > 0 && (
        <StyledBreakdownRow>
          <StyledBreakdownCol>Voucher</StyledBreakdownCol>
          <StyledBreakdownCol>
            {formatCurrency({
              value: -discountAmountInPence,
              valueInPence: true,
              showPence: hasDecimals(discountAmountInPence / 100),
            })}
          </StyledBreakdownCol>
        </StyledBreakdownRow>
      )}
      <StyledBreakdownRow>
        <StyledBreakdownCol $strong>Total</StyledBreakdownCol>
        <StyledBreakdownCol $strong>
          {formatCurrency({
            value: calculatedPrices.totalInPence,
            valueInPence: true,
            showPence: hasDecimals(calculatedPrices.totalInPence / 100),
          })}
        </StyledBreakdownCol>
      </StyledBreakdownRow>
    </>
  )
}

export default ProbateOnlyQuoteBreakdown
